import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../lib/format';
import { formatMoney } from '../../../lib/money';
import { Barcode } from '../Barcode';
import { DocFrame, Row } from '../DocFrame';
import type { PrintDocProps } from '../print-types';
import { registerPrintDoc } from '../print-registry';
import type { RefundPrint } from '../types';

export function RefundDoc({ data, size, onReady }: PrintDocProps<RefundPrint>): ReactNode {
  const { t } = useTranslation();
  const hasBarcode = size === '80mm';
  return (
    <DocFrame title={t('print.title.refund')} meta={data.meta} hasBarcode={hasBarcode} onReady={onReady}>
      <Row label={t('print.number')} value={data.refundNo} />
      <Row label={t('print.dateTime')} value={formatDateTime(data.paidAt)} />
      <section className="print-block">
        <div className="print-label">{t('print.customer')}</div>
        {data.customer === null ? (
          <div>{t('print.walkIn')}</div>
        ) : (
          <>
            <div>{data.customer.name}</div>
            {data.customer.phone === null ? null : <div className="print-small">{data.customer.phone}</div>}
          </>
        )}
      </section>
      <section className="print-block">
        <div>{t('print.refundOf', { no: data.source.no, reason: data.reason })}</div>
        <div className="print-small">{t(`print.source.${data.source.type}`)}</div>
      </section>
      <section className="print-block print-totals">
        <Row label={t('print.method.label')} value={t(`print.method.${data.method}`)} />
        {data.reference === null ? null : <Row label={t('print.reference')} value={data.reference} />}
        <Row label={t('print.amount')} value={<strong>{formatMoney(data.amount)}</strong>} />
        <Row label={t('print.approvedBy')} value={data.approvedBy} />
        <Row label={t('print.paidBy')} value={data.paidBy} />
      </section>
      <section className="print-block print-signature">
        <div>{t('print.receivedBy')}</div>
        <div className="print-sign-line" />
        <div>{t('print.icLast4')}</div>
        <div className="print-sign-line print-sign-short" />
        <div>{t('print.date')}</div>
        <div className="print-sign-line" />
      </section>
      {hasBarcode ? (
        <div className="print-barcode-wrap">
          <Barcode value={data.refundNo} onReady={onReady} />
        </div>
      ) : null}
    </DocFrame>
  );
}

registerPrintDoc<RefundPrint>('refund', RefundDoc);
